/**
 * Online Ordering — Kitxens flagship service page.
 *
 * Premium visual landing (Index AI / Digital Branding tier) — dark hero,
 * black narrative sections, orange accents, no walls of text.
 *
 * Editorial rule: numbers are portfolio ranges + honest industry benchmarks.
 * Never invented figures.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowDown,
  ArrowUpRight,
  ShoppingCart,
  Smartphone,
  Utensils,
  CreditCard,
  Gift,
  BarChart3,
  Cpu,
  CheckCircle2,
  XCircle,
  Percent,
  Wallet,
  Database,
  Star,
  TrendingUp,
  Zap,
} from 'lucide-react';
import SEO from '../components/SEO';
import HeroImage from '../components/HeroImage';
import { heroFor } from '../data/serviceImages';
import RelatedResources from '../components/RelatedResources';
import ServiceShowcase from '../components/ServiceShowcases';
import ContinueExploring from '../components/ContinueExploring';
import { useT } from '../i18n';

const COPY = {
  en: {
    seo_title: 'Zero-Commission Online Ordering for Restaurants | Kitxens',
    seo_desc:
      'Direct, branded, POS-native online ordering that beats third-party delivery apps on margin, data ownership and guest experience — operated by Kitxens.',
    eyebrow: 'DIRECT ONLINE ORDERING',
    title: 'Own the order. Own the guest.',
    tagline: 'A direct ordering surface tuned to your menu, your brand and your margins.',
    intro:
      'Kitxens builds and operates a direct online ordering channel that plugs straight into your POS — so every order feeds your kitchen, your loyalty and your P&L, not a delivery app.',
    ctaA: 'Get a Free Audit',
    ctaB: 'Talk to our Team',

    s1_eyebrow: 'THE HIDDEN COST',
    s1_title: 'Every third-party order pays a stranger 20–30%.',
    s1_body:
      'Delivery apps optimize for the app, not for you. Kitxens rebuilds the ordering flow so the margin and the guest data stay in your house.',

    s2_eyebrow: 'THE DIRECT ORDER STACK',
    s2_title: 'Six connected surfaces. One direct funnel.',
    s2_body: 'From the moment a guest opens your menu to the moment they reorder — one continuous flow.',

    s3_eyebrow: 'MOBILE-FIRST BY DEFAULT',
    s3_title: 'Where 78% of restaurant orders start.',
    s3_body: 'A checkout tuned for thumbs — three taps from menu to paid.',

    s4_eyebrow: 'POS INTEGRATION',
    s4_title: 'Orders flow straight into the kitchen.',
    s4_body: 'No tablets. No re-keying. Native connections to the POS platforms your team already runs.',

    s5_eyebrow: 'CONVERSION TUNING',
    s5_title: 'Every screen benchmarked against the last.',
    s5_body: 'Continuous A/B testing on menu structure, upsells, minimums, delivery vs. pickup CTAs.',

    s6_eyebrow: 'OUTCOMES',
    s6_title: 'What direct ordering compounds to.',
    s6_body: 'Ranges observed across the Kitxens portfolio in the first two quarters after activation.',

    s7_eyebrow: 'BUILT INTO REVENUE GROWTH',
    s7_title: 'Direct orders feed the loyalty flywheel.',
    s7_body: 'Every direct order enriches guest data — which powers lifecycle emails, offers and repeat visits.',

    s8_title: "Let's take the order back.",
    s8_body: 'A free audit maps your third-party leak and shows the direct-order upside in real dollars.',
    s8_cta: 'Get a Free Audit',
  },
  es: {
    seo_title: 'Pedidos Online Sin Comisiones para Restaurantes | Kitxens',
    seo_desc:
      'Pedidos online directos, con tu marca y conectados a tu POS — que superan a las apps de delivery en margen, datos y experiencia. Operado por Kitxens.',
    eyebrow: 'PEDIDOS ONLINE DIRECTOS',
    title: 'Dueño del pedido. Dueño del cliente.',
    tagline: 'Una superficie de pedidos directos alineada a tu menú, marca y márgenes.',
    intro:
      'Kitxens construye y opera un canal de pedido directo enchufado a tu POS — para que cada pedido alimente tu cocina, tu lealtad y tu P&L, no la app de delivery.',
    ctaA: 'Solicita Auditoría Gratis',
    ctaB: 'Habla con el Equipo',

    s1_eyebrow: 'EL COSTE OCULTO',
    s1_title: 'Cada pedido de terceros le paga un 20–30% a un desconocido.',
    s1_body:
      'Las apps de delivery optimizan para la app, no para ti. Kitxens reconstruye el flujo para que el margen y los datos se queden en casa.',

    s2_eyebrow: 'EL STACK DE PEDIDO DIRECTO',
    s2_title: 'Seis superficies conectadas. Un embudo directo.',
    s2_body: 'Desde que un cliente abre el menú hasta que vuelve a pedir — un flujo continuo.',

    s3_eyebrow: 'MOBILE-FIRST POR DEFECTO',
    s3_title: 'Donde arranca el 78% de los pedidos.',
    s3_body: 'Un checkout pensado para pulgares — tres taps del menú al pago.',

    s4_eyebrow: 'INTEGRACIÓN CON EL POS',
    s4_title: 'Los pedidos entran directo a cocina.',
    s4_body: 'Sin tablets. Sin doble entrada. Conexiones nativas con los POS que ya usa tu equipo.',

    s5_eyebrow: 'TUNING DE CONVERSIÓN',
    s5_title: 'Cada pantalla medida contra la anterior.',
    s5_body: 'A/B testing continuo en menú, upsells, mínimos y CTAs de delivery vs. pickup.',

    s6_eyebrow: 'RESULTADOS',
    s6_title: 'A qué compone el pedido directo.',
    s6_body: 'Rangos observados en la cartera Kitxens los primeros dos trimestres tras la activación.',

    s7_eyebrow: 'CONECTADO AL REVENUE GROWTH',
    s7_title: 'Pedidos directos alimentan la lealtad.',
    s7_body: 'Cada pedido directo enriquece los datos del cliente — que impulsan lifecycle, ofertas y visitas repetidas.',

    s8_title: 'Recuperemos el pedido.',
    s8_body: 'Una auditoría gratuita cuantifica tu fuga de comisiones y el upside directo en dólares reales.',
    s8_cta: 'Solicita Auditoría Gratis',
  },
};

// Shared header
function SectionHeader({ eyebrow, title, body, align = 'center', dark = false }) {
  return (
    <div className={`max-w-2xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
      <p className="text-[11px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">{eyebrow}</p>
      <h2
        className={`mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1] ${
          dark ? 'text-white' : 'text-neutral-900'
        }`}
      >
        {title}
      </h2>
      {body && (
        <p className={`mt-5 text-[15.5px] sm:text-base leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>
          {body}
        </p>
      )}
    </div>
  );
}

function FlowNode({ icon: Icon, label, i, dark = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
      className="group flex flex-col items-center text-center"
    >
      <div
        className={`w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl flex items-center justify-center transition-all ${
          dark
            ? 'bg-white/[0.04] border border-white/10 group-hover:border-[#F77F11] group-hover:bg-white/[0.08]'
            : 'bg-white border border-orange-100 shadow-[0_18px_50px_-30px_rgba(247,127,17,0.4)] group-hover:border-[#F77F11] group-hover:shadow-[0_18px_50px_-20px_rgba(247,127,17,0.6)]'
        }`}
      >
        <Icon className="w-7 h-7 text-[#F77F11]" />
      </div>
      <p className={`mt-3 text-[13.5px] font-semibold tracking-[-0.005em] leading-tight max-w-[110px] ${dark ? 'text-neutral-200' : 'text-neutral-800'}`}>
        {label}
      </p>
    </motion.div>
  );
}

export default function OnlineOrderingPage() {
  const { lang } = useT();
  const c = COPY[lang] || COPY.en;
  const hero = heroFor('online-ordering');

  const stack = [
    { icon: Utensils, label: lang === 'es' ? 'Menú' : 'Menu' },
    { icon: Smartphone, label: lang === 'es' ? 'Móvil' : 'Mobile' },
    { icon: CreditCard, label: lang === 'es' ? 'Pago' : 'Payment' },
    { icon: Cpu, label: 'POS' },
    { icon: Gift, label: lang === 'es' ? 'Lealtad' : 'Loyalty' },
    { icon: BarChart3, label: lang === 'es' ? 'Analítica' : 'Analytics' },
  ];

  const posSystems = ['Toast', 'Square', 'Clover', 'Lightspeed', 'Aloha', 'Revel', 'Micros', 'TouchBistro'];

  const metrics = [
    { k: '+22–60%', v: lang === 'es' ? 'Share de pedidos directos' : 'Direct order share' },
    { k: '-15–25%', v: lang === 'es' ? 'Coste neto de comisión' : 'Net commission cost' },
    { k: '+18–40%', v: lang === 'es' ? 'Ticket medio' : 'Average ticket size' },
    { k: '100%', v: lang === 'es' ? 'Datos propios de cliente' : 'First-party guest data' },
  ];

  const thirdPartyPains = [
    { label: lang === 'es' ? '20–30% comisión por pedido' : '20–30% commission per order' },
    { label: lang === 'es' ? 'Sin acceso a datos del cliente' : 'Zero access to guest data' },
    { label: lang === 'es' ? 'Sin control sobre la marca' : 'No control over brand experience' },
    { label: lang === 'es' ? 'Menú y precios recortados' : 'Menu and pricing throttled' },
    { label: lang === 'es' ? 'Reputación en manos de otros' : 'Reputation held by the app' },
  ];
  const kitxensWins = [
    { label: lang === 'es' ? 'Sin comisión de terceros' : 'No third-party commission' },
    { label: lang === 'es' ? 'Datos de cliente 100% tuyos' : '100% of guest data is yours' },
    { label: lang === 'es' ? 'Marca completa end-to-end' : 'Full brand end-to-end' },
    { label: lang === 'es' ? 'Menú, precios y upsells libres' : 'Free menu, pricing and upsells' },
    { label: lang === 'es' ? 'Reseñas y lealtad en casa' : 'Reviews and loyalty in-house' },
  ];

  const tuning = [
    { icon: Zap, t: lang === 'es' ? 'Velocidad de checkout' : 'Checkout speed', b: lang === 'es' ? 'Objetivo bajo 3 taps hasta pagar.' : 'Target: under 3 taps to pay.' },
    { icon: Utensils, t: lang === 'es' ? 'Estructura de menú' : 'Menu structure', b: lang === 'es' ? 'Categorías, orden y modificadores probados semana a semana.' : 'Categories, order and modifiers tested weekly.' },
    { icon: TrendingUp, t: lang === 'es' ? 'Upsells y bundles' : 'Upsells & bundles', b: lang === 'es' ? 'Recomendaciones contextuales sobre el hábito del guest.' : 'Contextual recommendations tuned to guest habit.' },
    { icon: Percent, t: lang === 'es' ? 'Mínimos y umbrales' : 'Minimums & thresholds', b: lang === 'es' ? 'Delivery vs. pickup calibrados por horario y ticket.' : 'Delivery vs. pickup calibrated by daypart and ticket.' },
    { icon: Wallet, t: lang === 'es' ? 'Métodos de pago' : 'Payment methods', b: lang === 'es' ? 'Apple Pay, Google Pay, tarjeta guardada, gift cards.' : 'Apple Pay, Google Pay, saved card, gift cards.' },
    { icon: Star, t: lang === 'es' ? 'Prueba social' : 'Social proof', b: lang === 'es' ? 'Reseñas y platos populares donde el guest decide.' : 'Reviews and popular dishes surfaced at decision time.' },
  ];

  return (
    <div className="pt-28 bg-white" data-testid="page-online-ordering">
      <SEO path="/services/online-ordering" title={c.seo_title} description={c.seo_desc} />

      {/* ═══════ 0. HERO (dark) ═══════ */}
      <section className="relative overflow-hidden bg-neutral-950 text-white pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #F77F11 1.2px, transparent 1.8px), radial-gradient(circle at 80% 70%, #F77F11 1.2px, transparent 1.8px)',
            backgroundSize: '140px 140px, 190px 190px',
          }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <p className="text-[#F77F11] text-[11px] tracking-[0.22em] font-bold uppercase">{c.eyebrow}</p>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]">{c.title}</h1>
              <p className="mt-5 text-xl sm:text-2xl text-neutral-200 font-semibold leading-snug max-w-2xl">{c.tagline}</p>
              <p className="mt-5 text-neutral-400 text-base sm:text-lg leading-relaxed max-w-2xl">{c.intro}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/#audit"
                  className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm transition-all"
                  data-testid="oo-cta-audit"
                >
                  {c.ctaA} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
                <Link
                  to="/contact#contact-form"
                  className="inline-flex items-center gap-1 border border-white/25 hover:bg-white/10 text-white h-12 px-7 rounded-full text-[15px] font-semibold"
                  data-testid="oo-cta-talk"
                >
                  {c.ctaB} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <HeroImage image={hero} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ 1. THIRD-PARTY vs DIRECT (compare, black bg) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-900 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s1_eyebrow} title={c.s1_title} body={c.s1_body} dark />
          <div className="mt-14 grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
              <p className="text-[11px] tracking-[0.22em] font-bold uppercase text-neutral-400">
                {lang === 'es' ? 'Con delivery apps' : 'With delivery apps'}
              </p>
              <ul className="mt-6 space-y-3">
                {thirdPartyPains.map((p) => (
                  <li key={p.label} className="flex items-start gap-3 text-neutral-300 text-[14.5px]">
                    <XCircle className="w-4 h-4 text-red-400 mt-1 shrink-0" />
                    <span>{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-[#F77F11]/30 bg-[#F77F11]/[0.08] p-8 shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)]">
              <p className="text-[11px] tracking-[0.22em] font-bold uppercase text-[#F77F11]">
                {lang === 'es' ? 'Con pedido directo Kitxens' : 'With Kitxens direct ordering'}
              </p>
              <ul className="mt-6 space-y-3">
                {kitxensWins.map((p) => (
                  <li key={p.label} className="flex items-start gap-3 text-neutral-200 text-[14.5px]">
                    <CheckCircle2 className="w-4 h-4 text-[#F77F11] mt-1 shrink-0" />
                    <span>{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 2. STACK NODES (light) ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s2_eyebrow} title={c.s2_title} body={c.s2_body} />
          <div className="mt-14 grid grid-cols-3 sm:grid-cols-6 gap-x-2 gap-y-10">
            {stack.map((n, i) => (
              <FlowNode key={n.label} icon={n.icon} label={n.label} i={i} />
            ))}
          </div>
        </div>
      </section>

      <ServiceShowcase slug="online-ordering" lang={lang} />

      {/* ═══════ 3. MOBILE-FIRST (dark) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 30%, #F77F11 1.2px, transparent 1.8px), radial-gradient(circle at 75% 70%, #F77F11 1.2px, transparent 1.8px)',
            backgroundSize: '150px 150px, 200px 200px',
          }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s3_eyebrow} title={c.s3_title} body={c.s3_body} dark />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { k: '78%', v: lang === 'es' ? 'de pedidos empiezan en móvil' : 'of orders start on mobile' },
              { k: '< 2s', v: lang === 'es' ? 'de tiempo de carga objetivo' : 'target load time' },
              { k: '3 taps', v: lang === 'es' ? 'del menú al pago' : 'from menu to paid' },
              { k: 'A / B', v: lang === 'es' ? 'testing continuo' : 'continuous testing' },
            ].map((m, i) => (
              <motion.div
                key={m.k}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 4) * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:border-[#F77F11]/50 transition-colors"
              >
                <p className="text-[#F77F11] text-2xl sm:text-3xl font-bold tracking-[-0.02em]">{m.k}</p>
                <p className="mt-2 text-neutral-300 text-[13px] leading-relaxed">{m.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 4. POS INTEGRATIONS (light) ═══════ */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s4_eyebrow} title={c.s4_title} body={c.s4_body} />
          <div className="mt-14 flex flex-wrap justify-center gap-3">
            {posSystems.map((name, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 8) * 0.04 }}
                className="inline-flex items-center gap-2 border border-neutral-200 rounded-full px-5 h-11 bg-white hover:border-[#F77F11]/60 hover:text-[#F77F11] transition-colors"
              >
                <Cpu className="w-3.5 h-3.5 text-[#F77F11]" />
                <span className="text-[13.5px] font-semibold text-neutral-700 tracking-[-0.005em]">{name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 5. CONVERSION TUNING (black) ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-900 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s5_eyebrow} title={c.s5_title} body={c.s5_body} dark />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tuning.map((t, i) => (
              <motion.div
                key={t.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-[#F77F11]/50 hover:bg-white/[0.06] transition-all"
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-[#F77F11]/15 items-center justify-center">
                  <t.icon className="w-5 h-5 text-[#F77F11]" />
                </span>
                <h3 className="mt-5 text-white font-semibold text-[17px] leading-snug tracking-[-0.005em]">{t.t}</h3>
                <p className="mt-2 text-neutral-400 text-[14px] leading-relaxed">{t.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 6. RESULTS ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-950 text-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s6_eyebrow} title={c.s6_title} body={c.s6_body} dark />
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map((m, i) => (
              <motion.div
                key={m.k}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#F77F11]/50 transition-colors p-6"
              >
                <p className="text-[#F77F11] text-2xl sm:text-3xl font-bold tracking-[-0.02em] leading-none">{m.k}</p>
                <p className="mt-3 text-neutral-300 text-[13px] leading-relaxed">{m.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 7. REVENUE GROWTH CONNECTION (light) ═══════ */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-orange-50/40 to-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader eyebrow={c.s7_eyebrow} title={c.s7_title} body={c.s7_body} />
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-10">
            {[
              { icon: ShoppingCart, label: lang === 'es' ? 'Pedido directo' : 'Direct order' },
              { icon: Database, label: lang === 'es' ? 'Datos de cliente' : 'Guest data' },
              { icon: Gift, label: lang === 'es' ? 'Lealtad' : 'Loyalty' },
              { icon: TrendingUp, label: lang === 'es' ? 'Revisita' : 'Repeat visit' },
            ].map((n, i) => (
              <React.Fragment key={n.label}>
                <FlowNode icon={n.icon} label={n.label} i={i} />
              </React.Fragment>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/restaurant-revenue-growth"
              className="inline-flex items-center gap-1.5 text-[#F77F11] hover:text-[#d8651a] font-semibold text-[15px]"
              data-testid="oo-link-revenue"
            >
              {lang === 'es' ? 'Ver Revenue Growth' : 'See Revenue Growth'} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ 8. RELATED RESOURCES ═══════ */}
      <RelatedResources serviceSlug="online-ordering" lang={lang} />

      {/* ═══════ 9. FINAL CTA ═══════ */}
      <section className="py-24 lg:py-32 relative overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 40%, #F77F11 1.3px, transparent 2px), radial-gradient(circle at 70% 60%, #F77F11 1.3px, transparent 2px)',
            backgroundSize: '150px 150px, 210px 210px',
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center px-6">
          <h2 className="text-4xl sm:text-5xl lg:text-[52px] font-bold tracking-[-0.02em] leading-[1.05]">{c.s8_title}</h2>
          <p className="mt-6 text-neutral-300 text-lg leading-relaxed">{c.s8_body}</p>
          <Link
            to="/#audit"
            className="mt-10 inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-14 px-9 rounded-full text-[16px] font-semibold shadow-lg transition-all"
            data-testid="oo-final-cta"
          >
            {c.s8_cta} <ArrowRight className="w-5 h-5 ml-1" />
          </Link>
        </div>
      </section>

      <ContinueExploring context="online-ordering" />
    </div>
  );
}
